import { Skeleton } from "antd";
import { createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  AddItemsPickerButton,
  AddItemsSelectionPill,
  drawerSelectionPillLabel,
} from "../../../shared/orderLineForm/components/addItemsPickerStyles";
import { createdLinks, describeItem, stepErrorMessage } from "./fulfilHelpers";

/** DOM node in the drawer footer where the open step puts its buttons. */
export const FulfilFooterContext = createContext(null);

/**
 * A step's footer: selection pill on the left, actions on the right. Inside
 * the drawer it renders into the shared footer bar; elsewhere (tests, the
 * inline pick-list part) it renders in place.
 *
 * @param {{pill?: *, inline?: boolean, children: *}} props
 */
export const StepFooter = ({ pill, inline = false, children }) => {
  const node = useContext(FulfilFooterContext);
  const content = (
    <>
      <span className="pillSlot">{pill}</span>
      <div className="footerButtons">{children}</div>
    </>
  );
  return node && !inline ? createPortal(content, node) : <InlineFooter>{content}</InlineFooter>;
};

/**
 * "2 out of 3 selected" pill, as on the purchase order Add items drawer.
 *
 * @param {{selected: number, total: number, units?: string, kind?: string}} props
 */
export const SelectionPill = ({ selected, total, units, kind = "lines" }) => (
  <AddItemsSelectionPill $active={selected > 0}>
    {drawerSelectionPillLabel(selected, total, kind)}
    {units ? ` · ${units} units` : ""}
  </AddItemsSelectionPill>
);

/**
 * Loading, error, "can't do this yet" and "nothing to do" states around a
 * step's content. Renders `children` only when there are lines to act on.
 *
 * @param {{
 *   query: {data: ?Object, isLoading: boolean, isError: boolean, error: *, refetch: function()},
 *   emptyText: string,
 *   children: *,
 * }} props
 */
export const StepState = ({ query, emptyText, children }) => {
  const { data, isLoading, isError, error, refetch } = query;
  if (isLoading) return <Skeleton active paragraph={{ rows: 5 }} />;
  if (isError) {
    return (
      <Notice $tone="danger" role="alert">
        <p>{stepErrorMessage(error)}</p>
        <AddItemsPickerButton onClick={refetch}>Try again</AddItemsPickerButton>
      </Notice>
    );
  }
  if (data?.blocker) {
    return (
      <Notice $tone="warning" role="status">
        {data.blocker}
      </Notice>
    );
  }
  const hasLines = data?.lines?.length > 0;
  const onlySkipped = !hasLines && data?.skipped?.length > 0;
  return (
    <>
      {hasLines ? children : null}
      {onlySkipped ? (
        <Notice $tone="warning" role="status">
          Nothing can go through this step right now. The reasons are listed below.
        </Notice>
      ) : null}
      {!hasLines && !onlySkipped ? <Notice role="status">{emptyText}</Notice> : null}
      <SkippedList skipped={data?.skipped} />
    </>
  );
};

/**
 * Lines left out of a step, with the reason for each.
 *
 * @param {{skipped: ?Array<!Object>}} props
 */
export const SkippedList = ({ skipped }) => {
  if (!skipped?.length) return null;
  return (
    <Skipped>
      <h4>Not included ({skipped.length})</h4>
      <ul>
        {skipped.map((item, index) => (
          <li key={`${item.line_id ?? "x"}-${index}`}>
            {describeItem(item) ? <strong>{describeItem(item)}: </strong> : null}
            {item.reason}
          </li>
        ))}
      </ul>
    </Skipped>
  );
};

/**
 * What a step just did: summary, any lines that failed and why, links to the
 * documents created, and the next step.
 *
 * @param {{
 *   result: !Object,
 *   nextLabel?: string,
 *   onNext?: function(),
 *   onAgain: function(),
 *   inline?: boolean,
 * }} props `inline` keeps the buttons in place instead of the drawer footer.
 */
export const StepResult = ({ result, nextLabel, onNext, onAgain, inline = false }) => {
  const links = createdLinks(result.created);
  const anySucceeded = result.succeeded?.length > 0;
  return (
    <ResultBox role="status">
      <Notice $tone={anySucceeded ? "success" : "warning"}>{result.summary}</Notice>
      {links.length ? (
        <ul className="created">
          {links.map(link => (
            <li key={link.key}>
              <Link to={link.to} target="_blank" rel="noopener noreferrer">
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
      {result.failed?.length ? (
        <Skipped>
          <h4>Didn't go through</h4>
          <ul>
            {result.failed.map((item, index) => (
              <li key={`${item.id ?? "x"}-${index}`}>
                {describeItem(item) ? <strong>{describeItem(item)}: </strong> : null}
                {item.message}
              </li>
            ))}
          </ul>
        </Skipped>
      ) : null}
      <StepFooter inline={inline}>
        <AddItemsPickerButton onClick={onAgain}>Back to this step</AddItemsPickerButton>
        {onNext && anySucceeded ? (
          <AddItemsPickerButton type="primary" onClick={onNext}>
            {nextLabel}
          </AddItemsPickerButton>
        ) : null}
      </StepFooter>
    </ResultBox>
  );
};

export const StepOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  align-items: flex-end;
  margin: 16px 0 0;
  padding: 12px 16px;
  border: 1px solid var(--PEARL, #ecf1f4);
  border-radius: 6px;
  background: var(--MIST, #f7f8f9);

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    color: var(--ASH, #676879);
    margin: 0;
  }

  label.inline {
    flex-direction: row;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    color: var(--GREY, #4a4a68);
  }

  input[type="text"],
  input[type="date"] {
    height: 32px;
    padding: 4px 8px;
    border: 1px solid #d5deea;
    border-radius: 6px;
    font-size: 13px;
    color: var(--DARK-GREY, #0e0e2c);
    background: #fff;
  }

  .terms {
    font-size: 13px;
    color: var(--GREY, #4a4a68);
  }
`;

export const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: var(--DARK-GREY, #0e0e2c);
  margin: 0 0 4px;
`;

export const SectionHint = styled.p`
  color: var(--ASH, #676879);
  font-size: 13px;
  margin: 0 0 16px;
`;

const TONES = {
  success: { bg: "#eaf7f0", border: "#b7e4c9", color: "#14532d" },
  warning: { bg: "#fff7e6", border: "#ffd591", color: "#7c3d00" },
  danger: { bg: "#fff1f0", border: "#ffa39e", color: "#a8071a" },
  info: { bg: "var(--CLOUD, #fcfdfe)", border: "var(--PEARL, #ecf1f4)", color: "#4a4a68" },
};

const Notice = styled.div`
  padding: 12px 14px;
  border-radius: 6px;
  font-size: 14px;
  background: ${({ $tone = "info" }) => TONES[$tone].bg};
  border: 1px solid ${({ $tone = "info" }) => TONES[$tone].border};
  color: ${({ $tone = "info" }) => TONES[$tone].color};

  p {
    margin: 0 0 8px;
  }
`;

const Skipped = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed var(--PEARL, #ecf1f4);

  h4 {
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ASH, #676879);
  }

  ul {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    color: var(--ASH, #676879);
  }
`;

const ResultBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .created {
    margin: 0;
    padding-left: 18px;
  }
`;

const InlineFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;

  .footerButtons {
    display: flex;
    gap: 8px;
  }
`;
